import { MapPin, Clock, Instagram } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { getWhatsAppLink, resolveImg, scrollToId } from "@/lib/site";
import { useLanguage } from "@/lib/i18n";

export default function FooterSection() {
  const { t, content } = useLanguage();

  return (
    <footer
      id="contato"
      data-testid="footer-section"
      className="border-t border-[#E9C46A]/10 bg-[#082F38] text-[#F4EBD9]"
    >
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 md:grid-cols-3 lg:px-10">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="grid h-11 w-11 place-items-center overflow-hidden rounded-full ring-2 ring-[#E9C46A]/50">
              <img src={resolveImg(content.images.footerLogo)} alt="Logo Quiosque Rainha do Mar" className="h-full w-full object-cover" />
            </span>
            <div className="leading-none">
              <p className="font-cabinet text-lg font-extrabold tracking-tight">Rainha do Mar</p>
              <p className="mt-1 font-jetmono text-[10px] uppercase tracking-[0.25em] text-[#E9C46A]">
                {t.footer.brandSub}
              </p>
            </div>
          </div>
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-[#F4EBD9]/60">
            {t.footer.blurb}
          </p>
          <a
            data-testid="footer-instagram-link"
            href={content.contact.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-[#E9C46A]/35 px-5 py-2.5 font-cabinet text-sm font-bold text-[#E9C46A] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#E9C46A] hover:bg-[#E9C46A] hover:text-[#082F38]"
          >
            <Instagram size={15} />
            {content.contact.instagramHandle}
          </a>
        </div>

        <div>
          <h4 className="flex items-center gap-2 font-jetmono text-[11px] uppercase tracking-[0.22em] text-[#E9C46A]">
            <Clock size={13} />
            {t.footer.hoursTitle}
          </h4>
          <ul className="mt-5 space-y-2.5 text-sm text-[#F4EBD9]/70" data-testid="footer-hours">
            {t.footer.hours.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <button
            data-testid="footer-reserve-link"
            onClick={() => scrollToId("reservar")}
            className="mt-6 font-cabinet font-bold text-[#E76F51] underline-offset-4 transition-colors hover:text-[#E9C46A] hover:underline"
          >
            {t.footer.reserveCta}
          </button>
        </div>

        <div>
          <h4 className="flex items-center gap-2 font-jetmono text-[11px] uppercase tracking-[0.22em] text-[#E9C46A]">
            <MapPin size={13} />
            {t.footer.whereTitle}
          </h4>
          <p className="mt-5 text-sm leading-relaxed text-[#F4EBD9]/70" data-testid="footer-address">
            {t.footer.addressLine1}
            <br />
            {t.footer.addressLine2}
            <br />
            {t.footer.addressLine3}
          </p>
          <a
            data-testid="footer-whatsapp-link"
            href={getWhatsAppLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 font-cabinet text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:brightness-95"
          >
            <WhatsAppIcon size={15} />
            {t.footer.whatsappCta}
          </a>
        </div>
      </div>

      <div className="border-t border-[#F4EBD9]/10">
        <div className="mx-auto max-w-7xl px-5 py-5 sm:px-8 lg:px-10">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-jetmono text-[11px] uppercase tracking-[0.18em] text-[#F4EBD9]/45">
              © 2026 Quiosque Rainha do Mar · Sete Fontes, Ubatuba/SP
            </p>
            <p className="font-playfair text-sm italic text-[#E9C46A]/80">
              {t.footer.madeWith}
            </p>
          </div>

          <a
            data-testid="footer-dev-credit"
            href="https://www.instagram.com/ernstudioofc?xtok=ank3OWxlejIwNzVm"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex items-center justify-center gap-1.5 opacity-60 transition-opacity hover:opacity-100"
            aria-label="Visitar Instagram da ERN Studio"
            title="Visitar Instagram da ERN Studio"
          >
            <span className="font-jetmono text-[9px] uppercase tracking-[0.1em] text-[#F4EBD9]">
              Desenvolvido por
            </span>
            <img
              src="/images/ern-logo-transparent.svg"
              alt=""
              aria-hidden="true"
              className="h-[24px] w-[64px] shrink-0 object-contain"
            />
            <span className="font-jetmono text-[9px] font-medium uppercase tracking-[0.12em] text-[#E9C46A]" aria-hidden="true">
              Studio
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
}
